import { useState, useEffect } from "react";
import { type Vacancy, deleteVacancy } from "../../lib/api/vacancies";
import { api } from "../../lib/api/_client";
import { useT } from "../../lib/i18n/useT";
import { useAppStore } from "../../store/app";
import { extractVacancyFromScreenshot } from "../../lib/ai/vacancy-extraction";

interface VacancyDetailModalProps {
  vacancy: Vacancy;
  repoPath: string;
  onClose: (deleted?: boolean) => void;
  onStartWorkflow?: (vacancyId: string) => Promise<void>;
  hasWorkflow?: boolean;
}

export default function VacancyDetailModal({ vacancy: initialVacancy, repoPath, onClose, onStartWorkflow, hasWorkflow }: VacancyDetailModalProps) {
  const t = useT();
  const settings = useAppStore((s) => s.settings);
  const [currentVacancy, setCurrentVacancy] = useState(initialVacancy);
  const vacancy = currentVacancy;
  const [reextracting, setReextracting] = useState(false);
  const [startingWf, setStartingWf] = useState(false);
  const [activeTab, setActiveTab] = useState<"details" | "requirements" | "routes" | "evidence">("details");
  const [detailsSubTab, setDetailsSubTab] = useState<"summary" | "full_text">("summary");
  const [activeReqSubTab, setActiveReqSubTab] = useState<"hard" | "preferred" | "competencies">("hard");
  const [activeRouteId, setActiveRouteId] = useState<string>(vacancy.routes[0]?.id || "");
  const [screenshotBase64] = useState<string | null>(null);
  const [loadingImage, setLoadingImage] = useState(false);
  const [imageError, setImageError] = useState<string | null>(null);

  async function handleDelete() {
    if (!repoPath) return;
    const confirmed = window.confirm(t("detail_modal.action.delete_confirm"));
    if (confirmed) {
      try {
        await deleteVacancy(repoPath, currentVacancy.id);
        onClose(true);
      } catch (err) {
        console.error("Failed to delete vacancy", err);
      }
    }
  }

  async function handleRecoverText() {
    if (!repoPath || reextracting) return;
    
    const apiKey = settings?.anthropic_api_key || "";
    const model = settings?.anthropic_model || "gemini-2.5-flash";

    if (!apiKey) {
      alert("AI API Key is missing. Please configure it in Settings.");
      return;
    }

    setReextracting(true);
    try {
      // v2.0: seed screenshots aren't stored in Blob Storage yet, so the
      // re-extract flow only works when the browser still has the base64 in
      // memory (from a fresh capture). Loading from persisted storage is
      // pending Blob integration.
      const b64 = screenshotBase64;
      if (!b64) {
        throw new Error(
          "No screenshot available. Re-capture from the Vacancy Evaluator to re-extract."
        );
      }

      const extracted = await extractVacancyFromScreenshot(
        apiKey,
        model,
        b64,
        selectedRoute?.url,
        settings?.prompts?.vacancy_extraction,
      );

      if (extracted.full_text) {
        const updatedVacancy = {
          ...currentVacancy,
          full_text: extracted.full_text,
        };
        // Persist via the API — v2.0 vacancies.full_text lives in the
        // `extracted` JSON column server-side.
        await api("PUT", `/api/vacancies/${currentVacancy.id}`, {
          extracted: { full_text: extracted.full_text },
        });
        setCurrentVacancy(updatedVacancy);
      } else {
        throw new Error("No text was returned by the AI extraction model.");
      }
    } catch (err: any) {
      console.error("Failed to recover vacancy text", err);
      alert("Failed to recover text: " + (err?.message || err));
    } finally {
      setReextracting(false);
    }
  }

  const selectedRoute = vacancy.routes.find((r) => r.id === activeRouteId) || vacancy.routes[0];

  // v1.7 loaded persisted seed screenshots from disk. v2.0 doesn't yet
  // store screenshots (Blob Storage integration pending) — this effect
  // is a no-op until that lands.
  useEffect(() => {
    if (activeTab !== "evidence") return;
    setLoadingImage(false);
    setImageError(null);
    if (!screenshotBase64) {
      setImageError(
        t("detail_modal.evidence.load_error", { error: "Screenshot storage not yet enabled in the web version" }),
      );
    }
  }, [activeTab, selectedRoute, screenshotBase64, t]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/60 backdrop-blur-xs overflow-y-auto p-4 animate-in fade-in duration-200">
      <div className="bg-white rounded-2xl border border-slate-200/80 shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6 md:p-8 animate-in zoom-in-95 duration-200">
        
        {/* Modal Header */}
        <div className="flex items-center justify-between border-b border-slate-100 pb-4 mb-6">
          <div>
            <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
              {vacancy.client}
            </span>
            <h2 className="text-lg font-bold text-slate-800 tracking-tight mt-0.5">
              {vacancy.title}
            </h2>
          </div>
          <button
            onClick={() => onClose()}
            className="text-slate-400 hover:text-slate-600 text-lg font-bold focus:outline-none cursor-pointer p-1"
          >
            ✕
          </button>
        </div>

        {/* Tab Navigation */}
        <div className="flex border-b border-slate-200 mb-6 gap-1.5">
          {([
            { id: "details", labelKey: "detail_modal.tab.details" },
            { id: "requirements", labelKey: "detail_modal.tab.requirements" },
            { id: "routes", labelKey: "detail_modal.tab.routes" },
            { id: "evidence", labelKey: "detail_modal.tab.evidence" },
          ] as const).map((tab) => {
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2.5 -mb-px border text-xs font-bold uppercase tracking-wider rounded-t-xl transition-all duration-150 cursor-pointer focus:outline-none ${
                  isActive
                    ? "bg-white border-slate-200 border-b-white text-slate-900 shadow-2xs"
                    : "bg-slate-50/55 border-slate-100 border-b-slate-200 text-slate-400 hover:bg-slate-50 hover:text-slate-650"
                }`}
              >
                {t(tab.labelKey)}
              </button>
            );
          })}
        </div>

        {/* Details Tab */}
        {activeTab === "details" && (
          <div className="space-y-6 animate-in fade-in duration-100">
            
            {/* Details Sub-Tabs (Segmented Control style) */}
            <div className="bg-slate-100/70 p-1 rounded-xl flex gap-1 w-full max-w-2xl border border-slate-200/40 mb-6">
              {(["summary", "full_text"] as const).map((sub) => (
                <button
                  key={sub}
                  type="button"
                  onClick={() => setDetailsSubTab(sub)}
                  className={`flex-1 py-2 rounded-lg text-[10px] md:text-xs font-semibold tracking-wider transition-all cursor-pointer focus:outline-none uppercase ${
                    detailsSubTab === sub
                      ? "bg-white text-slate-950 shadow-xs border border-slate-200/35"
                      : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  {t(`detail_modal.subtab.${sub}` as any)}
                </button>
              ))}
            </div>

            {/* Summary Sub-Panel */}
            {detailsSubTab === "summary" && (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-6 animate-in fade-in duration-100">
                <div className="space-y-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">{t("detail_modal.label.location")}</span>
                  <span className="text-xs font-semibold text-slate-800">📍 {vacancy.location || "N/A"}</span>
                </div>
                <div className="space-y-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">{t("detail_modal.label.hours")}</span>
                  <span className="text-xs font-semibold text-slate-800">🕒 {vacancy.hours_per_week}h</span>
                </div>
                <div className="space-y-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">{t("detail_modal.label.remote")}</span>
                  <span className="text-xs font-semibold text-slate-800 capitalize">🏠 {vacancy.remote_policy || "N/A"}</span>
                </div>
                <div className="space-y-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">{t("detail_modal.label.zzp")}</span>
                  <span className="text-xs font-semibold text-slate-800">
                    {vacancy.zzp_allowed ? `✅ ${t("detail_modal.value.yes_allowed")}` : `❌ ${t("detail_modal.value.no")}`}
                  </span>
                </div>
                <div className="space-y-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">{t("detail_modal.label.dutch")}</span>
                  <span className="text-xs font-semibold text-slate-800">🗣️ {vacancy.dutch_language_requirement || "N/A"}</span>
                </div>
                <div className="space-y-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">{t("detail_modal.label.start_date")}</span>
                  <span className="text-xs font-semibold text-slate-800">
                    📅 {vacancy.start_date === "Immediately" ? t("detail_modal.value.immediately") : (vacancy.start_date || "Direct")}
                  </span>
                </div>
                <div className="space-y-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">{t("detail_modal.label.duration")}</span>
                  <span className="text-xs font-semibold text-slate-800">⏳ {t("detail_modal.value.months", { count: vacancy.duration_months })}</span>
                </div>
                <div className="space-y-1">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">{t("detail_modal.label.added_at")}</span>
                  <span className="text-xs font-semibold text-slate-800">
                    📅 {new Date(vacancy.added_at).toLocaleDateString()}
                  </span>
                </div>
              </div>
            )}

            {/* Vacancy Text Sub-Panel */}
            {detailsSubTab === "full_text" && (
              <div className="flex flex-col gap-4 w-full animate-in fade-in duration-100">
                <div className="border border-slate-100 rounded-xl bg-slate-50 p-5 max-h-[300px] overflow-y-auto w-full">
                  <div className="text-xs text-slate-700 whitespace-pre-wrap font-sans leading-relaxed">
                    {currentVacancy.full_text || (
                      <span className="text-slate-450 italic">
                        {t("detail_modal.full_text.empty")}
                      </span>
                    )}
                  </div>
                </div>
                {!currentVacancy.full_text && (
                  <button
                    type="button"
                    disabled={reextracting}
                    onClick={handleRecoverText}
                    className="self-start inline-flex items-center gap-2 rounded-lg bg-indigo-650 hover:bg-indigo-750 text-white px-4 py-2 text-xs font-semibold shadow-2xs hover:shadow-xs transition-all cursor-pointer focus:outline-none disabled:bg-indigo-350 disabled:cursor-wait"
                  >
                    {reextracting ? (
                      <>
                        <span className="h-3.5 w-3.5 animate-spin rounded-full border-2 border-indigo-200 border-t-white" />
                        {t("detail_modal.action.reextracting")}
                      </>
                    ) : (
                      <>
                        <span>🔍</span>
                        {t("detail_modal.action.reextract")}
                      </>
                    )}
                  </button>
                )}
              </div>
            )}

          </div>
        )}

        {/* Requirements Tab */}
        {activeTab === "requirements" && (
          <div className="space-y-6 animate-in fade-in duration-100">
            
            {/* Requirements Sub-Tabs (Segmented Control style) */}
            <div className="bg-slate-100/70 p-1 rounded-xl flex gap-1 w-full max-w-2xl border border-slate-200/40 mb-6">
              {(["hard", "preferred", "competencies"] as const).map((sub) => (
                <button
                  key={sub}
                  type="button"
                  onClick={() => setActiveReqSubTab(sub)}
                  className={`flex-1 py-2 rounded-lg text-[10px] md:text-xs font-semibold tracking-wider transition-all cursor-pointer focus:outline-none uppercase ${
                    activeReqSubTab === sub
                      ? "bg-white text-slate-950 shadow-xs border border-slate-200/35"
                      : "text-slate-500 hover:text-slate-800"
                  }`}
                >
                  {t(`detail_modal.req.${sub}` as any)}
                </button>
              ))}
            </div>

            {/* Hard Requirements Sub-Panel */}
            {activeReqSubTab === "hard" && (
              <div className="flex flex-col gap-3.5 animate-in fade-in duration-100">
                <div className="flex flex-wrap gap-1.5">
                  {vacancy.requirements.hard.length === 0 ? (
                    <span className="text-xs text-slate-400 italic">{t("detail_modal.req.empty.hard")}</span>
                  ) : (
                    vacancy.requirements.hard.map((req, idx) => (
                      <span key={idx} className="rounded-md bg-rose-50 border border-rose-100/50 px-2 py-0.5 text-xs font-medium text-rose-700">
                        {req}
                      </span>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Preferred Requirements Sub-Panel */}
            {activeReqSubTab === "preferred" && (
              <div className="flex flex-col gap-3.5 animate-in fade-in duration-100">
                <div className="flex flex-wrap gap-1.5">
                  {vacancy.requirements.preferred.length === 0 ? (
                    <span className="text-xs text-slate-400 italic">{t("detail_modal.req.empty.preferred")}</span>
                  ) : (
                    vacancy.requirements.preferred.map((req, idx) => (
                      <span key={idx} className="rounded-md bg-amber-50 border border-amber-100/50 px-2 py-0.5 text-xs font-medium text-amber-700">
                        {req}
                      </span>
                    ))
                  )}
                </div>
              </div>
            )}

            {/* Competencies Sub-Panel */}
            {activeReqSubTab === "competencies" && (
              <div className="flex flex-col gap-3.5 animate-in fade-in duration-100">
                <div className="flex flex-wrap gap-1.5">
                  {vacancy.requirements.competencies.length === 0 ? (
                    <span className="text-xs text-slate-400 italic">{t("detail_modal.req.empty.competencies")}</span>
                  ) : (
                    vacancy.requirements.competencies.map((req, idx) => (
                      <span key={idx} className="rounded-md bg-indigo-50 border border-indigo-100/50 px-2 py-0.5 text-xs font-medium text-indigo-700">
                        {req}
                      </span>
                    ))
                  )}
                </div>
              </div>
            )}

          </div>
        )}

        {/* Routes Tab */}
        {activeTab === "routes" && (
          <div className="space-y-4 animate-in fade-in duration-100">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="border-b border-slate-100 text-slate-400 text-[10px] uppercase tracking-wider">
                    <th className="py-2 font-bold">{t("detail_modal.routes.portal")}</th>
                    <th className="py-2 font-bold">{t("detail_modal.routes.deadline")}</th>
                    <th className="py-2 font-bold">{t("detail_modal.routes.rate")}</th>
                    <th className="py-2 font-bold text-right">{t("detail_modal.routes.action")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50 text-xs">
                  {vacancy.routes.map((route) => (
                    <tr key={route.id} className="hover:bg-slate-50/50">
                      <td className="py-3 font-semibold text-slate-800">{route.portal}</td>
                      <td className="py-3 text-slate-600">
                        {new Date(route.deadline).toLocaleString(undefined, {
                          day: "numeric",
                          month: "short",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </td>
                      <td className="py-3 text-slate-800 font-medium">
                        {route.rate ? `€${route.rate}/h` : "N/A"}
                      </td>
                      <td className="py-3 text-right">
                        <a
                          href={route.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 rounded-md bg-indigo-50 border border-indigo-100/30 px-2 py-0.5 text-xs font-semibold text-indigo-600 hover:text-indigo-800 transition-colors"
                        >
                          {t("detail_modal.routes.open_link")}
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Evidence Tab */}
        {activeTab === "evidence" && (
          <div className="space-y-4 animate-in fade-in duration-100">
            
            {/* Route Selector (if multiple routes exist) */}
            {vacancy.routes.length > 1 && (
              <div className="flex items-center gap-2 bg-slate-50 p-2 rounded-xl border border-slate-100 max-w-sm">
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider pl-1">{t("detail_modal.evidence.route")}</span>
                <select
                  value={activeRouteId}
                  onChange={(e) => setActiveRouteId(e.target.value)}
                  className="form-select flex-1 py-1 text-xs border-slate-200 bg-white"
                >
                  {vacancy.routes.map((r) => (
                    <option key={r.id} value={r.id}>
                      {r.portal} (Deadline: {new Date(r.deadline).toLocaleDateString()})
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Evidence Viewer */}
            <div className="border border-slate-100 rounded-xl bg-slate-50 p-2 overflow-hidden flex items-center justify-center min-h-[300px]">
              {loadingImage ? (
                <div className="flex flex-col items-center gap-2">
                  <span className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-slate-800" />
                  <p className="text-[10px] text-slate-400 font-semibold uppercase tracking-wider">{t("detail_modal.evidence.loading")}</p>
                </div>
              ) : imageError ? (
                <p className="text-xs text-red-500 font-semibold">{imageError}</p>
              ) : screenshotBase64 ? (
                <img
                  src={screenshotBase64}
                  alt={`Vacancy evidence from ${selectedRoute?.portal}`}
                  className="max-w-full max-h-[500px] object-contain rounded-lg border border-slate-200/50 shadow-xs"
                />
              ) : (
                <p className="text-xs text-slate-400 italic">{t("detail_modal.evidence.empty")}</p>
              )}
            </div>
          </div>
        )}



        {/* Bottom Actions */}
        <div className="flex justify-between border-t border-slate-100 pt-6 mt-8">
          <button
            type="button"
            onClick={handleDelete}
            className="rounded-lg bg-rose-50 hover:bg-rose-100 text-rose-600 hover:text-rose-700 px-4.5 py-2 text-xs font-semibold border border-rose-200/50 transition-colors cursor-pointer focus:outline-none"
          >
            {t("detail_modal.action.delete")}
          </button>

          <div className="flex items-center gap-2">
            {onStartWorkflow && !hasWorkflow && (
              <button
                type="button"
                disabled={startingWf}
                onClick={async () => {
                  setStartingWf(true);
                  await onStartWorkflow(currentVacancy.id);
                  onClose();
                }}
                className="rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white px-4.5 py-2 text-xs font-semibold shadow-xs hover:shadow-sm transition-all cursor-pointer focus:outline-none disabled:bg-indigo-300 disabled:cursor-wait"
              >
                {startingWf ? "Starting…" : t("workflow.start")}
              </button>
            )}
            {hasWorkflow && (
              <span className="rounded-lg bg-emerald-50 border border-emerald-100/60 text-emerald-700 px-3 py-2 text-xs font-semibold">
                ✓ Workflow active
              </span>
            )}
            <button
              type="button"
              onClick={() => onClose()}
              className="rounded-lg bg-slate-900 hover:bg-slate-800 text-white px-4.5 py-2 text-xs font-semibold shadow-xs hover:shadow-sm transition-all cursor-pointer focus:outline-none"
            >
              {t("common.close")}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
}
